<script setup lang="ts">
import { useChapterHeadings } from "../composables/useChapterHeadings";
/**
 * Main-pane host for `.zwmap` (#268). Binds the session document buffer to
 * the map island and wires Database entity pick / open. Kept separate from
 * `DocumentEditor.vue` so prose chrome does not load for maps.
 *
 * Mounts the island only after the session's active file is the routed map.
 * Otherwise heal-on-read would push map JSON into `session.doc` while
 * `active_path` still names the previous `.zwdoc`, and the next flush would
 * clobber that entry on disk.
 */
import { computed, watch } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { isJust, isNothing, just, nothing, type Maybe } from "../lib/maybe";
import { displayFileName } from "../lib/fileNames";
import { isMapFileName } from "../lib/mapDocument";
import {
  pendingMapLandmarkFocus,
  takeMapLandmarkFocus,
} from "../lib/mapLandmarkFocus";
import {
  MANUSCRIPT_SCENE_NONE,
  manuscriptSceneSelectOptions,
} from "../lib/manuscriptScenes";
import type { StoryArcMomentRef } from "../lib/mapStoryPath";
import { sortArcMoments } from "../lib/entityArc";
import { entityEffectiveColor } from "../lib/entityColors";
import { scenePath } from "../lib/zwConfig";
import { databaseMapFileName } from "../router/projectPaths";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import { useOutlineEntitiesStore } from "../stores/useOutlineEntitiesStore";
import { useProjectWorkspaceStore } from "../stores/useProjectWorkspaceStore";
import { useSceneOpeningsStore } from "../stores/useSceneOpeningsStore";
import type { MapEditorManuscriptChapter } from "./MapEditor.port";
import MapEditor from "./MapEditor.vue";

const { chapterLabel } = useChapterHeadings();
const route = useRoute();
const session = useEditorSessionStore();
const outlineEntities = useOutlineEntitiesStore();
const workspace = useProjectWorkspaceStore();
const sceneOpenings = useSceneOpeningsStore();

const { doc, rootPath, mobileShell, activeDatabase, activeMap } =
  storeToRefs(session);

/** Same access pattern as EntityArcPanel; avoids storeToRefs on this getter. */
const chapters = computed(() => session.chapters);

const routedMapName = computed(() =>
  databaseMapFileName(String(route.params.name ?? "")),
);

const mapFileName = computed(() => activeDatabase.value);

/** True when Pinia already points at this route's map (open finished). */
const mapBufferReady = computed(
  () =>
    activeMap.value === true &&
    isJust(activeDatabase.value) &&
    activeDatabase.value.value === routedMapName.value &&
    routedMapName.value.length > 0,
);

/** Landmark to select once this map's buffer is ready (Relationships click). */
const focusLandmarkId = computed((): Maybe<string> => {
  if (mapBufferReady.value === false) return nothing;
  const pending = pendingMapLandmarkFocus.value;
  if (isNothing(pending)) return nothing;
  if (pending.value.mapFile !== routedMapName.value) return nothing;
  return just(pending.value.landmarkId);
});

function onFocusLandmarkApplied(): void {
  takeMapLandmarkFocus(routedMapName.value);
}

const entityOptions = computed(() =>
  outlineEntities.databaseEntryFields
    .filter(
      (entry) => entry.isMap === false && isMapFileName(entry.name) === false,
    )
    .map((entry) => ({
      value: entry.name,
      type: entry.fields.type,
      label: displayFileName(entry.name),
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, "en", { sensitivity: "base" }),
    ),
);

function openEntityFile(fileName: string): void {
  void workspace.onSelectProjectDatabase(fileName);
}

// Keep scene opening words warm while the map host is mounted.
watch(
  () => session.manuscript,
  () => {
    sceneOpenings.ensureLoaded();
  },
  { immediate: true },
);

const sceneOptions = computed(() => {
  // Subscribe to async opening loads (see useSceneOpeningsStore.openingsByPath).
  const openings = sceneOpenings.openingsByPath;
  return manuscriptSceneSelectOptions(chapters.value, (path) => {
    void openings[path];
    return sceneOpenings.openingFor(path);
  }).filter((opt) => opt.value !== MANUSCRIPT_SCENE_NONE);
});

const manuscriptChapters = computed((): MapEditorManuscriptChapter[] => {
  const openings = sceneOpenings.openingsByPath;
  return chapters.value.map((chapter) => ({
    folder: chapter.folder,
    title: chapterLabel(chapter.folder),
    scenes: chapter.scenes.map((scene) => {
      const path = scenePath(chapter.folder, scene.file);
      void openings[path];
      const fromOptions = sceneOptions.value.find((opt) => opt.value === path);
      return {
        path,
        label:
          typeof fromOptions !== "undefined"
            ? fromOptions.label
            : displayFileName(scene.file),
      };
    }),
  }));
});

const arcMoments = computed((): StoryArcMomentRef[] => {
  const out: StoryArcMomentRef[] = [];
  for (const entry of outlineEntities.databaseEntryFields) {
    if (entry.isMap === true) continue;
    for (const moment of sortArcMoments(entry.fields.arc)) {
      if (isNothing(moment.link)) continue;
      const file = moment.link.value.file.trim();
      if (file.length === 0) continue;
      out.push({
        scenePath: file,
        entityFile: entry.name,
        momentLabel: moment.moment,
        color: entityEffectiveColor(entry.fields.color, entry.fields.type),
      });
    }
  }
  return out;
});

function openSceneFile(scenePathValue: string): void {
  void workspace.onSelectProjectFile(scenePathValue);
}
</script>

<template>
  <MapEditor
    v-if="mapBufferReady"
    v-model="doc"
    class="absolute inset-0 z-1 min-h-0"
    :readonly="mobileShell"
    :root-path="rootPath"
    :map-file-name="mapFileName"
    :entity-options="entityOptions"
    :scene-options="sceneOptions"
    :manuscript-chapters="manuscriptChapters"
    :arc-moments="arcMoments"
    :focus-landmark-id="focusLandmarkId"
    :on-select-entity-file="just(openEntityFile)"
    :on-select-scene-file="just(openSceneFile)"
    :on-focus-landmark-applied="just(onFocusLandmarkApplied)"
  />
  <div
    v-else
    class="map-editor-host-pending absolute inset-0 z-1"
    aria-busy="true"
    aria-label="Opening map"
  />
</template>

<style scoped>
.map-editor-host-pending {
  background: var(--bg);
}
</style>
